import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  InstallationAddress,
  installationAddressSchema,
  InstallationCreatePayload,
  installationCreatePayloadSchema,
  installationGeneralInformationSchema,
  InstallationLocalAddress,
  installationLocalAddressSchema,
  installationReportingPeriodBasisSchema
} from '../../api/InstallationModel'
import { Button, GoodsFamily, ReportingPeriodBasis } from '@/common'
import { useSession } from '@/shared/hooks/useSession'
import useScrollToHash from '@/shared/hooks/useScrollToHash'
import { useCreateInstallation } from '../../hooks/useInstallations'
import AddressCard from '@/components/AddressCard'
import GeneralInfoCard from '../components/GeneralInfoCard'
import ReportingPeriodBasisCard from '../components/ReportingPeriodBasisCard'
import { useAppState } from '@/app/hooks/useAppState'
import LocalAddressCard from '@/components/LocalAddressCard'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'

function loadInstallation(localStorageKey: string): InstallationCreatePayload | null {
  const installation = window.localStorage.getItem(localStorageKey)
  if (installation) {
    const parsedInstallation = JSON.parse(installation) as InstallationCreatePayload
    return parsedInstallation
  }
  return null
}

export default function InstallationCreatePage({ localStorageKey }: { localStorageKey: string }) {
  const { setSelectedInstallationId } = useAppState()
  const { mutateAsync: createInstallation, isPending: isCreatingInstallation } =
    useCreateInstallation()
  useScrollToHash()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    session: {
      organization: { placeOfRegistration: organizationPlaceOfRegistration }
    }
  } = useSession()

  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organizationPlaceOfRegistration
  )

  const [creationState, setCreationState] = useState<{
    name: string
    localName: string
    goodsFamily: string | GoodsFamily
    basisForReportingPeriod: string | ReportingPeriodBasis
    startMonth: number | null
    monthInterval: number | null
    street?: string
    streetNumber?: string
    postCode?: string
    street2?: string
    city: string
    poBox?: string
    country: string
    unlocode?: string
    longitude: number
    latitude: number
    localStreet?: string
    localStreetNumber?: string
    localPostCode?: string
    localCity: string
    localPoBox?: string
    localStreet2?: string
  }>(
    loadInstallation(localStorageKey) ?? {
      name: '',
      localName: '',
      goodsFamily: '',
      basisForReportingPeriod: '',
      startMonth: null,
      monthInterval: null,
      street: '',
      streetNumber: '',
      street2: '',
      postCode: '',
      city: '',
      poBox: '',
      country: organizationPlaceOfRegistration || '',
      unlocode: '',
      longitude: 0,
      latitude: 0,
      localStreet: '',
      localStreetNumber: '',
      localPostCode: '',
      localCity: '',
      localPoBox: '',
      localStreet2: ''
    }
  )

  const onGeneralInfoChange = useCallback(
    (payload: { name: string; localName: string; goodsFamily: string | GoodsFamily }) => {
      setCreationState({
        ...creationState,
        ...payload
      })
    },
    [creationState]
  )

  const onReportingPeriodChange = useCallback(
    (payload: {
      basisForReportingPeriod: string | ReportingPeriodBasis
      startMonth: number | null
      monthInterval: number | null
    }) => {
      setCreationState({
        ...creationState,
        ...payload
      })
    },
    [creationState]
  )

  const onAddressChange = useCallback(
    (payload: InstallationAddress) => {
      setCreationState({
        ...creationState,
        ...payload,
        localStreet: payload.street,
        localStreetNumber: payload.streetNumber,
        localPostCode: payload.postCode,
        localCity: payload.city,
        localPoBox: payload.poBox,
        localStreet2: payload.street2,
        latitude: Number(payload.latitude),
        longitude: Number(payload.longitude)
      })
    },
    [creationState]
  )

  const onAddressWithLocalChange = useCallback(
    (payload: InstallationAddress & InstallationLocalAddress) => {
      setCreationState({
        ...creationState,
        ...payload,
        latitude: Number(payload.latitude),
        longitude: Number(payload.longitude)
      })
    },
    [creationState]
  )

  const saveAndNavigateToDashboard = useCallback(async () => {
    const validatedData = installationCreatePayloadSchema.safeParse(creationState)
    if (!validatedData.success) {
      console.error(validatedData.error)
      return
    }
    const installation = await createInstallation(validatedData.data)

    window.localStorage.removeItem(localStorageKey)
    setSelectedInstallationId(installation.id)
    navigate(`/dashboard/${installation.id}`)
  }, [createInstallation, creationState, navigate, localStorageKey, setSelectedInstallationId])

  const progress = useMemo(
    () => [
      {
        title: t('installations:create.sections.generalInfo.title'),
        isComplete: installationGeneralInformationSchema.safeParse(creationState).success
      },
      {
        title: t('installations:create.sections.reportingPeriodBasis.title'),
        isComplete: installationReportingPeriodBasisSchema.safeParse(creationState).success
      },
      {
        title: t('installations:create.sections.address.title'),
        isComplete: doesOrganizationRequiresLocalAddress
          ? installationLocalAddressSchema.safeParse(creationState).success &&
            installationAddressSchema.safeParse(creationState).success
          : installationAddressSchema.safeParse(creationState).success
      }
    ],
    [creationState, t, doesOrganizationRequiresLocalAddress]
  )

  useEffect(() => {
    window.localStorage.setItem(localStorageKey, JSON.stringify(creationState))
  }, [creationState, localStorageKey])

  const isSaveInstallationEnabled = useMemo(() => {
    return progress.map(step => step.isComplete).every(Boolean)
  }, [progress])

  return (
    <div>
      <div className="relative px-6 py-5 space-y-4">
        <header className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">{t('installations:create.title')}</h2>
        </header>
        <div className="space-y-6">
          <GeneralInfoCard
            id="general-info"
            state={{
              name: creationState.name,
              localName: creationState.localName,
              goodsFamily: creationState.goodsFamily
            }}
            onChange={payload => onGeneralInfoChange(payload)}
            organizationPlaceOfRegistration={organizationPlaceOfRegistration}
          />
          <ReportingPeriodBasisCard
            id="reporting-period"
            state={{
              basisForReportingPeriod: creationState.basisForReportingPeriod,
              startMonth: creationState.startMonth,
              monthInterval: creationState.monthInterval
            }}
            onChange={payload => onReportingPeriodChange(payload)}
          />
          {!doesOrganizationRequiresLocalAddress ? (
            <AddressCard
              id="address"
              state={{
                street: creationState.street,
                streetNumber: creationState.streetNumber,
                street2: creationState.street2,
                postCode: creationState.postCode,
                city: creationState.city,
                poBox: creationState.poBox,
                country: creationState.country,
                unlocode: creationState.unlocode,
                latitude: creationState.latitude,
                longitude: creationState.longitude
              }}
              onChange={payload => onAddressChange(payload)}
            />
          ) : (
            <LocalAddressCard
              id="local-address"
              state={{
                street: creationState.street,
                streetNumber: creationState.streetNumber,
                street2: creationState.street2,
                postCode: creationState.postCode,
                city: creationState.city,
                poBox: creationState.poBox,
                country: creationState.country,
                unlocode: creationState.unlocode,
                latitude: creationState.latitude,
                longitude: creationState.longitude,
                localCity: creationState.localCity,
                localStreet: creationState.localStreet,
                localStreetNumber: creationState.localStreetNumber,
                localPostCode: creationState.localPostCode,
                localPoBox: creationState.localPoBox,
                localStreet2: creationState.localStreet2
              }}
              onChange={payload => onAddressWithLocalChange(payload)}
            />
          )}
        </div>
      </div>
      <footer className="flex justify-end gap-2 py-2 sticky bottom-0 bg-white px-6 border-y border-gray-200">
        <Button className="w-24" variant="neutral" onClick={() => navigate('/installations')}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          className="w-24"
          onClick={saveAndNavigateToDashboard}
          disabled={!isSaveInstallationEnabled}
          isLoading={isCreatingInstallation}
        >
          {t('common:actions.create')}
        </Button>
      </footer>
    </div>
  )
}
